import React, { useState } from 'react';
import { Box, Button, Paper, Skeleton, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { BotOpsHttpError } from './api/botOpsClient';
import { fmtAge, fmtIct } from './lib/ict';
import { FONT, GOLD, RADIUS, TONE, ToneName } from './lib/brand';
import { ink, tint } from './lib/tone';

/**
 * Full-width error card. Shown INSTEAD of the page when no snapshot has loaded
 * yet: never zeros (DASHBOARD.md §1.10). When a snapshot is already on screen a
 * failed poll shows the slim banner below and keeps the last good data.
 */
export function ErrorCard({ error, onRetry, lastGoodAt }: { error: BotOpsHttpError; onRetry: () => void; lastGoodAt?: number | null }) {
  const forbidden = error.status === 401 || error.status === 403;
  const disabled = error.code === 'BOT_OPS_DISABLED';
  const notMigrated = error.code === 'BOT_OPS_NOT_MIGRATED';
  const title = disabled
    ? 'Grokbot is switched off'
    : notMigrated
      ? 'Grokbot is not set up yet'
      : forbidden
        ? 'You do not have access to Grokbot'
        : 'Could not load dashboard data';
  const detail = disabled
    ? 'The feature flag BOT_OPS_ENABLED is off on the server.'
    : notMigrated
      ? 'The database tables have not been created (migration 1155).'
      : forbidden
        ? 'Grokbot needs the bot_operations permission.'
        : error.message;
  return (
    <Paper variant="outlined" role="alert" data-testid="bot-ops-error" sx={{ p: 3, borderColor: 'error.main', maxWidth: 720, mx: 'auto', mt: 4 }}>
      <Typography variant="h6" sx={{ fontFamily: FONT.display, fontWeight: 700, mb: 0.5 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{detail}</Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 2, fontFamily: FONT.mono }}>
        {error.status ? `HTTP ${error.status}` : 'No response'}{error.code ? ` · ${error.code}` : ''}
        {lastGoodAt ? ` · last good snapshot ${new Date(lastGoodAt).toLocaleTimeString()}` : ''}
      </Typography>
      {!disabled && !forbidden && <Button variant="contained" onClick={onRetry}>Retry</Button>}
    </Paper>
  );
}

/** Before the first snapshot arrives: not an error, not zeros. */
export function WaitingForData({ onRetry }: { onRetry: () => void }) {
  return (
    <Paper variant="outlined" data-testid="bot-ops-waiting" sx={{ p: 3, maxWidth: 720, mx: 'auto', mt: 4 }}>
      <Typography variant="h6" sx={{ fontFamily: FONT.display, fontWeight: 700, mb: 0.5 }}>Waiting for the first snapshot</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Grok Bot has not sent any data yet. It pushes a snapshot every 5 minutes (07:00 to 02:00 ICT), and one can be requested with Sync now once that is available.
      </Typography>
      <Button variant="outlined" onClick={onRetry}>Check again</Button>
    </Paper>
  );
}

const NOTICE_EDGE: Record<'warn' | 'bad' | 'info', string> = { warn: GOLD, bad: TONE.bad.fg, info: TONE.info.fg };
const NOTICE_ICON = { warn: WarningAmberIcon, bad: ErrorOutlineIcon, info: InfoOutlinedIcon };

/**
 * One thin line for a page notice (stale data, poll failure, source warnings, held new data).
 * About 28 px tall, so stacked notices stay small under the as-of bar.
 */
export function NoticeLine({ tone = 'warn', testId, children, action, role = 'status' }: {
  tone?: 'warn' | 'bad' | 'info';
  testId?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  role?: 'status' | 'alert';
}) {
  const t = TONE[tone as ToneName];
  const Icon = NOTICE_ICON[tone];
  return (
    <Box
      role={role}
      data-testid={testId}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.4, mb: 1, minHeight: 28,
        borderLeft: `4px solid ${NOTICE_EDGE[tone]}`, bgcolor: tint(t.bg), color: ink(t.fg),
        borderRadius: RADIUS.md, fontSize: 12.5, fontFamily: FONT.heading, fontWeight: 600, lineHeight: 1.4,
      }}
    >
      <Icon sx={{ fontSize: 16, color: NOTICE_EDGE[tone], flexShrink: 0 }} />
      <Box component="span" sx={{ minWidth: 0 }}>{children}</Box>
      <Box sx={{ flex: 1 }} />
      {action}
    </Box>
  );
}

const lineButton = { minWidth: 0, py: 0, px: 0.75, fontSize: 12.5, fontFamily: FONT.heading, fontWeight: 700, textTransform: 'none', lineHeight: 1.6 } as const;

/** A poll failed but a snapshot is on screen: say so, keep the data. */
export function PollErrorStrip({ error, onRetry }: { error: BotOpsHttpError; onRetry: () => void }) {
  return (
    <NoticeLine tone="bad" role="alert" testId="bot-ops-poll-error" action={<Button color="inherit" size="small" onClick={onRetry} sx={lineButton}>Retry</Button>}>
      Could not refresh ({error.status ? `HTTP ${error.status}` : 'no response'}) · showing the last data received.
    </NoticeLine>
  );
}

/** One line when the snapshot is over 2 h old (§1.10). */
export function StaleStrip({ minutes }: { minutes: number }) {
  return (
    <NoticeLine tone="warn" testId="bot-ops-stale">
      Data is {fmtAge(minutes)} old<Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}> · the ingest job may be down; showing the last snapshot received</Box>
      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}> · last snapshot shown</Box>
    </NoticeLine>
  );
}

/** One line, folded: "1 source warning · Show". The open state is local, not in the URL. */
export function WarningsStrip({ warnings }: { warnings: string[] }) {
  const [open, setOpen] = useState(false);
  if (!warnings.length) return null;
  const listId = 'bot-ops-warnings-list';
  return (
    <Box data-testid="bot-ops-warnings" sx={{ mb: 1 }}>
      <NoticeLine
        tone="warn"
        action={(
          <Button color="inherit" size="small" aria-expanded={open} aria-controls={listId} onClick={() => setOpen((o) => !o)} sx={lineButton}>
            {open ? 'Hide' : 'Show'}
          </Button>
        )}
      >
        {warnings.length} source warning{warnings.length === 1 ? '' : 's'}
      </NoticeLine>
      {open && (
        <Box id={listId} sx={{ mt: -0.5, px: 1.5, pl: 3.25, py: 0.5, color: ink(TONE.warn.fg) }}>
          {warnings.map((w) => <Box key={w} sx={{ fontSize: 12, fontFamily: FONT.mono, overflowWrap: 'anywhere' }}>{w}</Box>)}
        </Box>
      )}
    </Box>
  );
}

export function PageSkeleton() {
  return (
    <Box data-testid="bot-ops-skeleton" aria-busy="true" aria-label="Loading data">
      <Skeleton variant="rounded" height={44} sx={{ mb: 2 }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' }, gap: 1.5, mb: 2 }}>
        {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} variant="rounded" height={84} />)}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) 380px' }, gap: 2, mb: 2 }}>
        <Skeleton variant="rounded" height={320} />
        <Skeleton variant="rounded" height={320} />
      </Box>
      <Skeleton variant="rounded" height={360} />
    </Box>
  );
}

export const generatedLabel = (iso?: string | null) => fmtIct(iso);
